import React, { useMemo } from 'react'
import InfoButton from '../../common/InfoButton'
import { useTranslation } from 'react-i18next'
import { classData } from '../../../constants/ClassAnalyticsConstants'
import Loading from '../../Loading'

function ClassCardData({ selectedClass = {}, loading = false }) {
    return (
        <div>
            {loading && <LoadingContainer />}
            {!loading && !selectedClass && <LoadingContainer showEmptyData={true} />}
            {!loading && !!selectedClass && <DataContainer data={selectedClass} />}
        </div>
    )
}

export default ClassCardData

const DataContainer = ({ data }) => {
    const { t } = useTranslation()

    const prefix = useMemo(() => {
        if (!data?.name?.toLowerCase().includes('class')) {
            return t('Class')
        }
    }, [data, t])

    function formatToNumberAndLetters(inputString) {
        let cleanedInput = inputString.replace(/^(Grade|Class|Test)\s*/i, '').trim()

        if (/^[a-zA-Z]$/.test(cleanedInput)) {
            return cleanedInput.toUpperCase()
        }
        if (/^\d+$/.test(cleanedInput)) {
            return cleanedInput
        }

        if (/^\d+\s[a-z]$/.test(cleanedInput)) {
            const [numberPart, letterPart] = cleanedInput.split(/\s+/)
            return `${numberPart}${letterPart.toUpperCase()}`
        }

        if (/^\d+-[a-zA-Z]+$/.test(cleanedInput)) {
            const [numberPart, letterPart] = cleanedInput.split('-')
            const formattedNumber = numberPart.replace(/^0+/, '')
            return `${formattedNumber}${letterPart.toUpperCase()}`
        }

        const match = cleanedInput.match(/^([a-zA-Z])?-?(\d+)([a-zA-Z]*)$/)
        if (match) {
            const [_, prefixLetter, number, suffixLetters] = match

            const formattedNumber = number.replace(/^0+/, '')

            if (prefixLetter) {
                return `${formattedNumber}${suffixLetters || ''}${prefixLetter.toUpperCase()}`
            }

            if (suffixLetters) {
                return `${suffixLetters.toUpperCase()}${formattedNumber}`
            }

            return formattedNumber
        }

        return cleanedInput.slice(0, 5)
    }

    const loading = !data?.id

    return (
        <div className={`py-8 px-8 rounded-[24px] relative bg-[#D3D5FF]`} data-tour="basic-info">
            {/* <button className="absolute right-8 top-8">
                <InfoButton
                    color="#77768D"
                    text={t('Shows the total emotions logged by the class so far this year, with a simple breakdown into comfortable and uncomfortable emotions')}
                />
            </button> */}
            <div className="flex flex-row items-center gap-5">
                {/* <div className="flex items-center justify-center  h-[110px] min-w-[110px]">
                    <div className="flex items-center justify-center bg-white rounded-full h-[90px] min-w-[90px] max-w-[90px]">
                        <p className="text-darkenPurple text-[30px] leading-[30px] font-bold tracking-tight truncate">
                            {loading ? <Loading size={30} /> : formatToNumberAndLetters(data?.name ?? '')}
                        </p>
                    </div>
                </div> */}
                <p className="text-darkenPurple text-[30px] leading-[39px] font-semibold tracking-tight mt-2 line-clamp-2" title={`${data?.name ?? ''}`}>
                    {prefix} {data?.name ?? '-'}
                </p>
            </div>

            <div className="w-full h-[1px] bg-white mt-6"></div>
            <div className="bg-[#FFFFFF66]  flex flex-col gap-8 p-5 rounded-[16px] mt-8 ">
                <div className="flex flex-row gap-8  flex-1">
                    <div className="flex flex-col gap-1 flex-1 max-w-[200px]">
                        <p className="text-mediumGray text-[16px] leading-5 font-medium ">{t('teacher')}</p>
                        <p className="text-strongGray text-[20px] leading-[25px] font-semibold tracking-tight truncate flex-1" title={data?.teacher ?? ''}>
                            {data?.teacher?.first_name ?? '-'} {data?.teacher?.last_name ?? ''}
                        </p>
                    </div>
                </div>
                <div className="flex flex-row gap-8">
                    <div className="flex flex-col gap-1 max-w-[80px]">
                        <p className="text-mediumGray text-[16px] leading-5 font-medium capitalize">{t('year')}</p>
                        <p className="text-strongGray text-[20px] leading-[25px] font-semibold tracking-tight truncate" title={data?.year ?? ''}>
                            {data?.year ?? '-'}
                        </p>
                    </div>
                    <div className="flex flex-col gap-1">
                        <p className="text-mediumGray text-[16px] leading-5 font-medium ">{t('numberOfStudents')}</p>
                        <p className="text-strongGray text-[20px] leading-[25px] font-semibold tracking-tight truncate" title={data?.number_of_students ?? ''}>
                            {data?.students?.length ?? '-'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

const LoadingContainer = ({ showEmptyData = false }) => {
    const { t } = useTranslation()
    return (
        <div className="relative">
            <div className="blur">
                <DataContainer data={classData} />
            </div>
            {showEmptyData ? (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                    <div className="flex flex-col items-center">
                        <p className="text-[16px] leading-[25px] font-bold text-mudPurple ">{t('No data found')}</p>
                        <p className="text-[14px] leading-[25px] font-medium text-mudPurple px-5 text-center">
                            {t('This element will be updated once enough app-activity has been recorded')}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-10">
                    <Loading size={50} stroke={5} primaryColor="#7C82C3" secondaryColor="white" />
                </div>
            )}
        </div>
    )
}
